/*
 * CAPTURE-OWNED MODULE
 * ClassificationEvidencePanel — guided setup v2, turn one.
 *
 * THE FIRST PLACE THE PRODUCT SPEAKS FIRST. Everywhere else in Review the
 * machine states a result and waits to be corrected; here it says what it saw,
 * shows the evidence, and asks whether it was right. That is the whole of turn
 * one, and the three answers are the three things a reviewer can actually mean:
 * yes (dismiss, writes nothing), no but I know the type (the existing
 * correction flow), and no and the type does not exist yet (a declaration,
 * which slice 2 turns into a setup proposal).
 *
 * IT SAYS LESS RATHER THAN GUESSING. The evidence blobs already in the database
 * predate `matchedByType`/`winnerPhraseCount` and cannot be backfilled, so for
 * most documents in the queue today "what else was considered" is genuinely
 * unknowable. summarizeEvidence returns null for that, and null renders NOTHING
 * — not "none considered", which would be a claim about a comparison that was
 * never recorded. Likewise the "couldn't match anything" sentence appears only
 * where the blob PROVES it (every score zero), never merely where phrases are
 * missing.
 *
 * NO NETWORK CALLS LIVE HERE. The page owns fetching and owns the declaration
 * state; this component renders and calls back. That is what makes it testable
 * with the createRoot + act harness (capture-ui has no RTL).
 */

import React, { useCallback, useState } from 'react';
import { summarizeEvidence, confidenceTier } from '../utils/classificationEvidence';
import { formatDocType } from '../utils/formatDocType';
import './ClassificationEvidencePanel.css';

const MAX_LABEL = 80;
const MAX_WISHES = 2000;

// A raw timestamp is not an answer to "when did this get recorded?". Falls back
// to rendering nothing rather than an Invalid Date.
function formatWhen(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString();
}

/**
 * @param {string|null} documentType          the classified type
 * @param {number|null} confidence            document_type_confidence
 * @param {string|null} source                document_type_source ('user' = a reviewer set it)
 * @param {object|null} evidence              document_type_evidence, as served
 * @param {object|null} declaration           the PENDING capture_type_declarations row, if any
 * @param {boolean}     canBuild              may this user mint tenant schema?
 * @param {Function}    onPickDifferentType   opens the existing correction flow
 * @param {Function}    onDeclare             ({ declared_label, field_wishes }) => Promise
 * @param {boolean}     busy                  a declare is in flight
 * @param {string|null} error                 message from a failed declare
 * @param {Function}    onDismiss             "yes, that's right" — local only, writes nothing
 */
export default function ClassificationEvidencePanel({
  documentType,
  confidence,
  source,
  evidence,
  declaration,
  canBuild,
  onPickDifferentType,
  onDeclare,
  busy,
  error,
  onDismiss,
  onContinueSetup,
  setupBusy,
}) {
  const [showDeclareForm, setShowDeclareForm] = useState(false);
  const [label, setLabel] = useState('');
  const [wishes, setWishes] = useState('');

  const { winnerPhrases, runnersUp, nothingMatched, divisor } = summarizeEvidence(
    evidence,
    documentType
  );

  const setByUser = source === 'user';
  // A percent is shown only where a number was actually recorded. A user
  // correction writes no confidence by design, and rows predating the column
  // read as null — "0%" would be a fabrication in both cases.
  const hasConfidence = typeof confidence === 'number';

  const trimmedLabel = label.trim();

  const submit = useCallback(
    (e) => {
      if (e) e.preventDefault();
      if (!trimmedLabel || busy) return;
      const trimmedWishes = wishes.trim();
      onDeclare({
        declared_label: trimmedLabel,
        // Omitted, not null: the server schema is strict and field_wishes is
        // optional rather than nullable.
        field_wishes: trimmedWishes === '' ? undefined : trimmedWishes,
      });
    },
    [trimmedLabel, wishes, busy, onDeclare]
  );

  return (
    <div className="rd-instructions cep-panel">
      <div className="rd-instructions-label">What I saw</div>

      <div className="cep-seen">
        {setByUser ? (
          <span>
            Set by a reviewer: <strong>{formatDocType(documentType)}</strong>
          </span>
        ) : (
          <span>
            Classified as <strong>{formatDocType(documentType)}</strong>
            {hasConfidence && (
              <>
                {' — '}
                <span className={`cep-confidence cep-confidence--${confidenceTier(confidence)}`}>
                  {Math.round(confidence * 100)}% confident
                </span>
              </>
            )}
            {divisor != null && (
              <span className="cep-divisor">
                {' '}
                ({winnerPhrases.length} of {divisor} phrases matched)
              </span>
            )}
          </span>
        )}
      </div>

      {/* The sentence is earned, not assumed: summarizeEvidence sets this only
          where the blob proves every score was zero. */}
      {nothingMatched && (
        <div className="cep-nothing">
          I couldn&apos;t match anything I recognise in this document.
        </div>
      )}

      {winnerPhrases.length > 0 && (
        <div className="cep-chips">
          {winnerPhrases.map((phrase) => (
            <span className="cep-chip" key={phrase}>
              {phrase}
            </span>
          ))}
        </div>
      )}

      {/* runnersUp === null means "we cannot know" (a legacy blob) and renders
          nothing at all. Only a non-empty list is an assertion. */}
      {Array.isArray(runnersUp) && runnersUp.length > 0 && (
        <div className="cep-alternatives">
          Also considered:{' '}
          {runnersUp.map((r, i) => (
            <span key={r.type}>
              {i > 0 && ', '}
              {formatDocType(r.type)} ({r.phrases.join(', ')})
            </span>
          ))}
        </div>
      )}

      {declaration ? (
        <div className="cep-recorded">
          {/* This used to read "setup will pick this up", which over-promised:
              it implied an actor and a timeframe that did not exist. Since the
              2026-09-02 direction change, setup happens HERE, in this panel, in
              the same sitting — so the line says what actually happens next and
              offers the way to it. */}
          Recorded: &lsquo;{declaration.declared_label}&rsquo;
          {formatWhen(declaration.created_at) && (
            <span className="cep-recorded-when"> {formatWhen(declaration.created_at)}</span>
          )}
          <div className="cep-recorded-next">
            {setupBusy
              ? 'Drafting the setup for this document — this takes a few seconds…'
              : 'Next: review the document type and index fields, then create them.'}
          </div>
          {onContinueSetup && !setupBusy && (
            <div className="cep-actions">
              <button type="button" className="cep-btn cep-btn--primary" onClick={onContinueSetup}>
                Continue setup
              </button>
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="cep-question">Is that right?</div>

          <div className="cep-actions">
            <button type="button" className="cep-btn cep-btn--primary" onClick={onDismiss}>
              Yes, that&apos;s right
            </button>
            <button type="button" className="cep-btn" onClick={onPickDifferentType}>
              Pick a different type
            </button>
            {/* Builder-gated: a declaration is a request to mint tenant schema,
                and the server enforces the same gate (accessPolicy buildOnly).
                Hidden rather than disabled — offering a member a button that
                can only 403 is worse than not offering it. */}
            {canBuild && !showDeclareForm && (
              <button
                type="button"
                className="cep-btn"
                onClick={() => setShowDeclareForm(true)}
              >
                It&apos;s something new…
              </button>
            )}
          </div>

          {canBuild && showDeclareForm && (
            <form className="rd-reextract-panel cep-declare" onSubmit={submit}>
              <div className="rd-reextract-title">What kind of document is it?</div>
              <input
                id="cep-label"
                className="cep-input"
                type="text"
                value={label}
                maxLength={MAX_LABEL}
                placeholder="e.g. Certificate of Insurance"
                onChange={(e) => setLabel(e.target.value)}
              />

              <label className="cep-field-label" htmlFor="cep-wishes">
                What would you like captured from it? (optional)
              </label>
              <textarea
                id="cep-wishes"
                className="cep-textarea"
                rows={3}
                value={wishes}
                maxLength={MAX_WISHES}
                placeholder="e.g. policy number, insured name, expiry date"
                onChange={(e) => setWishes(e.target.value)}
              />

              <div className="cep-actions">
                <button
                  type="submit"
                  className="cep-btn cep-btn--primary cep-btn-submit"
                  disabled={busy || trimmedLabel === ''}
                >
                  {busy ? 'Recording…' : 'Submit'}
                </button>
                <button
                  type="button"
                  className="cep-btn"
                  onClick={() => setShowDeclareForm(false)}
                  disabled={busy}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </>
      )}

      {error && <div className="cep-error">{error}</div>}
    </div>
  );
}
